import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import './Ordermanagement.css';

const safeStatus = (v) => {
  const s = String(v ?? '').trim().toLowerCase();
  return s || 'pending';
};

const formatDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const statusColor = (st) => {
  const s = safeStatus(st);
  if (s === 'delivered')                                    return { bg: '#d4edda', text: '#155724' };
  if (s === 'cancelled')                                    return { bg: '#f8d7da', text: '#721c24' };
  if (s === 'out-for-delivery')                             return { bg: '#cce5ff', text: '#004085' };
  if (['accepted', 'confirmed', 'processing'].includes(s)) return { bg: '#d4edda', text: '#2d6a4f' };
  return { bg: '#fff3cd', text: '#856404' };
};

function OrderCard({ order }) {
  const st     = safeStatus(order?.status);
  const colors = statusColor(st);

  return (
    <div className="om-card">

      {/* ── Top: Order No + Status ── */}
      <div className="om-card-top">
        <span className="om-order-no">
          {order?.order_number || `#${order?.id}`}
        </span>
        <span
          className="om-status-badge"
          style={{ background: colors.bg, color: colors.text }}
        >
          {st.replace('-', ' ').toUpperCase()}
        </span>
      </div>

      {/* ── Amount (prominent) ── */}
      <div className="om-amount-row">
        <span className="om-amount">Rs. {Number(order?.total_amount || 0).toFixed(0)}</span>
        <span className="om-payment">{order?.payment_method || '—'}</span>
      </div>

      <div className="om-hr" />

      {/* ── Two column info grid ── */}
      <div className="om-info-grid">
        <div className="om-info-cell">
          <span className="om-info-label">👤 Customer</span>
          <span className="om-info-val">{order?.customer_name || '—'}</span>
        </div>
        <div className="om-info-cell">
          <span className="om-info-label">📞 Phone</span>
          <span className="om-info-val">{order?.customer_phone || order?.delivery_phone || '—'}</span>
        </div>
        <div className="om-info-cell om-info-cell--full">
          <span className="om-info-label">📍 Address</span>
          <span className="om-info-val">{order?.delivery_address || order?.deliveryAddress || '—'}</span>
        </div>
        <div className="om-info-cell">
          <span className="om-info-label">🕒 Placed</span>
          <span className="om-info-val">{formatDateTime(order?.created_at || order?.createdAt)}</span>
        </div>
        {order?.rider_name && (
          <div className="om-info-cell">
            <span className="om-info-label">🏍️ Rider</span>
            <span className="om-info-val">{order.rider_name}</span>
          </div>
        )}
        {order?.delivered_at && (
          <div className="om-info-cell">
            <span className="om-info-label">✅ Delivered</span>
            <span className="om-info-val">{formatDateTime(order.delivered_at)}</span>
          </div>
        )}
      </div>

    </div>
  );
}

export default function OrderManagement() {
  const { api } = useContext(AuthContext);

  const [orders, setOrders]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter]       = useState('all');

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    try {
      setError('');
      const endpoints = ['/orders/all', '/orders'];
      let success = false;

      for (const endpoint of endpoints) {
        try {
          const response = await api.get(endpoint);
          const data = Array.isArray(response.data)
            ? response.data
            : response.data?.orders || response.data?.data || [];
          setOrders(data);
          success = true;
          break;
        } catch (e) { continue; }
      }

      if (!success) throw new Error('Failed to load orders');
    } catch (err) {
      setError('Failed to load orders');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => { setRefreshing(true); fetchOrders(); };

  const filteredOrders = React.useMemo(() => {
    if (filter === 'active')
      return orders.filter(o => !['delivered', 'cancelled'].includes(safeStatus(o?.status)));
    if (filter === 'delivered')
      return orders.filter(o => safeStatus(o?.status) === 'delivered');
    if (filter === 'cancelled')
      return orders.filter(o => safeStatus(o?.status) === 'cancelled');
    return orders;
  }, [orders, filter]);

  const activeCount    = orders.filter(o => !['delivered','cancelled'].includes(safeStatus(o?.status))).length;
  const deliveredCount = orders.filter(o => safeStatus(o?.status) === 'delivered').length;
  const cancelledCount = orders.filter(o => safeStatus(o?.status) === 'cancelled').length;

  if (loading) return <LoadingSpinner message="Loading orders..." />;

  const tabs = [
    { value: 'all',       label: `All`,       count: orders.length    },
    { value: 'active',    label: `Active`,    count: activeCount      },
    { value: 'delivered', label: `Delivered`, count: deliveredCount   },
    { value: 'cancelled', label: `Cancelled`, count: cancelledCount   },
  ];

  return (
    <div className="om-page">

      {/* Header */}
      <div className="om-header">
        <div>
          <h1 className="om-header-title">📦 Order Management</h1>
          <p className="om-header-subtitle">{orders.length} total orders</p>
        </div>
        <button className="om-refresh-btn" onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? '🔄 Refreshing...' : '🔄 Refresh'}
        </button>
      </div>

      <div className="om-body">
        {error && (
          <div className="om-alert"><span>⚠️</span> {error}</div>
        )}

        {/* Filter Tabs */}
        <div className="om-tabs">
          {tabs.map(tab => (
            <button
              key={tab.value}
              className={`om-tab ${filter === tab.value ? 'active' : ''}`}
              onClick={() => setFilter(tab.value)}
            >
              {tab.label}
              <span className="om-tab-count">{tab.count}</span>
            </button>
          ))}
        </div>

        {/* ── Horizontal Grid ── */}
        {filteredOrders.length === 0 ? (
          <div className="om-empty">
            <span className="om-empty-icon">📦</span>
            <h2>No orders found</h2>
            <p>{filter === 'all' ? 'Orders will appear here once placed' : `No ${filter} orders`}</p>
          </div>
        ) : (
          <div className="om-grid">
            {filteredOrders.map(order => (
              <OrderCard key={order.id ?? order.order_number} order={order} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}